import React, { useEffect, useRef, useState } from 'react';
import { Group, Loader, Popover, Stack } from '@mantine/core';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import dayjs from 'dayjs';
import accounting from 'accounting';
import EmptyStateContainer from '../../EmptyStateContainer/EmptyStateContainer';
import RentIcon from '../../../../../../assets/images/module/general/RentIcon.svg';
import InfoIcon from '@material-ui/icons/Info';
import EmptyStateDashboard from '../../EmptyStateContainer/EmptyStateDashboard';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function RentIncomeControlDashboardTile({totalExpectedRent, totalReceivedRent, loader, navigate, totalPartialRent}){
    const chartRef = useRef();
    const containerRef = useRef();
    const [data, setData] = useState(null);
    const [expectedRent, setExpectedRent] = useState(totalExpectedRent);
    const [receivedRent, setReceivedRent] = useState(totalReceivedRent);
    const [remainingRent, setRemainingRent] = useState(totalPartialRent);
    const [tooltipData, setTooltipData] = useState(null);
    const [openPopover, setOpenPopover] = useState(false);
    const [hideLegend, setHideLegend] = useState(false);
    const [enableToolTip, setEnableToolTip] = useState(false);

    const options = {
        cutout: '65%',
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,
                external: (context)=> externalTooltipHandler(context)
            }
        },
        hover: {
            mode: null,
        }
    };

    const isNullOrUndefined=(value)=>{
        return value === null || value === undefined;
    }

    useEffect(() => {
        setExpectedRent(totalExpectedRent);
        setReceivedRent(totalReceivedRent);
        setRemainingRent(totalPartialRent);
    }, [totalExpectedRent, totalReceivedRent, totalPartialRent]);

    useEffect(() => {
        if(!isNullOrUndefined(expectedRent) && !isNullOrUndefined(receivedRent)){ // && overPaidRent === 0
            setData({
                labels: ['ausstehend', 'erhalten'],
                datasets: [
                    {
                    label: 'erwartet',
                    data: [remainingRent, receivedRent],
                    backgroundColor: [
                        '#8b88F5',
                        '#5555FF'
                    ],
                    hoverBackgroundColor: [
                        '#8b88F5',
                        '#5555FF'
                    ],
                    borderColor: '#fff',
                    borderWidth: 4, 
                    hoverOffset: 0
                    },
                ],
            });
        }
    }, [expectedRent, receivedRent, remainingRent]);

    useEffect(()=> {
        console.log('rent-income-control-dashboard-card: ', document.getElementById('rent-income-control-dashboard-card')?.offsetWidth);
        if(document.getElementById('rent-income-control-dashboard-card')?.offsetWidth < 336){
            setHideLegend(true);
        }else{
            setHideLegend(false);
        }
    }, [document.getElementById('rent-income-control-dashboard-card')?.offsetWidth]);

    useEffect(()=> {
        setTimeout(() => {
            setEnableToolTip(!loader);    
        }, 2000);
    }, [loader]);

    const renderTooltip=()=>{
        return (
        <div
        style={{
            position: 'absolute',
            left: tooltipData?.x,
            top: tooltipData?.y,
            backgroundColor: 'white',
            color: '#13135c',
            padding: '6px 10px',
            borderRadius: '4px',
            pointerEvents: 'none',
            transform: 'translate(-50%, -120%)',
            whiteSpace: 'nowrap',
            fontSize: 12,
            zIndex: 1000,
            boxShadow: '0 3px 3px rgba(0,0,0,.2)'
        }}
        >
            <div style={{color: tooltipData?.label === 'erhalten' ? '#5555FF' : '#8b88F5'}}><strong>{tooltipData?.label}</strong></div>
            <div style={{fontSize: '16px'}}>{accounting.formatMoney(tooltipData?.value, '€', 2, '.', ',', '%v %s')}</div>
        </div>);
    }

    const renderLegend=()=>{
        return (
            <Stack gap={10} onMouseEnter={()=> setOpenPopover(true)} onMouseLeave={()=> setOpenPopover(false)}>
                <Group justify="space-between" wrap="nowrap" style={{cursor: 'pointer'}} onClick={()=> handleSliceClick(null, null)}>
                    <Group gap={7} align='center' wrap="nowrap">
                        <div className='rent-income-control-dashboard-card-rounded-legend-total'/>
                        <span className='rent-income-control-dashboard-card-light-grey-text'>erwartet</span>
                    </Group>
                    <span className='rent-income-control-dashboard-card-dark-text'>{accounting.formatMoney(expectedRent, '€', 0, '.', ',', '%v %s')}</span>
                </Group>
                <div className='divider mb-0 mt-0'/>
                <Stack gap={7}>
                    <Group justify="space-between" wrap="nowrap" style={{cursor: 'pointer'}} onClick={()=> handleSliceClick('erhalten', null)}>
                        <Group gap={7} align='center' wrap="nowrap">
                            <div className='rent-income-control-dashboard-card-rounded-legend-paid'/>
                            <span className='rent-income-control-dashboard-card-light-grey-text'>erhalten</span>
                        </Group>
                        <span className='rent-income-control-dashboard-card-dark-text'>{accounting.formatMoney(receivedRent, '€', 0, '.', ',', '%v %s')}</span>
                    </Group>
                    <Group justify="space-between" wrap="nowrap" style={{cursor: 'pointer'}} onClick={()=> handleSliceClick('ausstehend', null)}>
                        <Group gap={7} align='center' wrap="nowrap">
                            <div className='rent-income-control-dashboard-card-rounded-legend-unpaid'/>
                            <span className='rent-income-control-dashboard-card-light-grey-text'>ausstehend</span>
                        </Group>                    
                        <span className='rent-income-control-dashboard-card-dark-text'>{accounting.formatMoney(remainingRent, '€', 0, '.', ',', '%v %s')}</span>
                    </Group>
                </Stack>
            </Stack>
        );
    }

    const externalTooltipHandler = (context) => {
        const { chart, tooltip } = context;

        if (!tooltip || !tooltip.opacity || !tooltip.dataPoints) {
          setTooltipData(null);
          return;
        }

        const containerRect = containerRef.current.getBoundingClientRect();

        const dataPoint = tooltip.dataPoints[0];
        const relativeX = tooltip.caretX - chart.canvas.offsetLeft;
        const relativeY = tooltip.caretY - chart.canvas.offsetTop;

        const newTooltip = {
            label: dataPoint.label,
            value: dataPoint.parsed,
            x: tooltip.caretX,
            y: tooltip.caretY,
        };

        // setTooltipData({
        //   label: dataPoint.label,
        //   value: dataPoint.parsed,
        //   x: relativeX,
        //   y: relativeY,
        // });

        setTooltipData((prev) => {
            if (
            !prev ||
            prev.label !== newTooltip.label ||
            prev.value !== newTooltip.value ||
            prev.x !== newTooltip.x ||
            prev.y !== newTooltip.y
            ) {
            return newTooltip;
            }
            return prev; // no change → no re-render
        });
    };

    const handleSliceClick = (label, value) => {
        if (label === 'erhalten') {
            navigate(`/user/rentIncomeTracking?startDate=${dayjs().startOf('month').format('YYYY-MM-DDT00:00:00')}&endDate=${dayjs().endOf('month').format('YYYY-MM-DDT00:00:00')}&paymentStatus=paid,partial_paid`)
        } else if (label === 'ausstehend') {
            navigate(`/user/rentIncomeTracking?startDate=${dayjs().startOf('month').format('YYYY-MM-DDT00:00:00')}&endDate=${dayjs().endOf('month').format('YYYY-MM-DDT00:00:00')}&paymentStatus=unpaid,partial_paid`)
        } else {
            navigate(`/user/rentIncomeTracking?startDate=${dayjs().startOf('month').format('YYYY-MM-DDT00:00:00')}&endDate=${dayjs().endOf('month').format('YYYY-MM-DDT00:00:00')}`);
        }
    };
    console.log('totalExpectedRent, totalReceivedRent: ', totalExpectedRent, totalReceivedRent);
    return (
        <Stack className='rent-income-control-dashboard-card' id='rent-income-control-dashboard-card' gap={5}>
            {/* <Group justify="space-between">
                <span className='rent-income-control-dashboard-card-title'>Mieteingänge</span>
                <span className='rent-income-control-dashboard-card-light-grey-text'>{dayjs().format('MMM YYYY')}</span>
            </Group> */}
            
            <Group 
            justify='center'
            //position={hideLegend ? 'center' : 'space-between'}
            className={(data && hideLegend) && 'ml-4'} //className='mt-4 mb-4' 
            align={hideLegend && 'flex-start'} 
            gap={hideLegend ? 0 : 16} wrap="nowrap">
                {loader ?
                <Loader size={200}/>
                :
                data ?
                <div ref={containerRef} style={{ position: 'relative', width: 180, height: 180 }}>
                    <Doughnut 
                    ref={chartRef} 
                    data={data} 
                    options={options}
                    style={{cursor: 'pointer'}}
                    onClick={(event) => {
                        if (!chartRef.current) return;
                        const chart = chartRef.current;
                  
                        // Get elements under the click
                        const elements = chart.getElementsAtEventForMode(
                          event,
                          'nearest', // mode
                          { intersect: true }, // options
                          true // use final dataset
                        );
                  
                        if (!elements.length) return;
                  
                        const { datasetIndex, index } = elements[0];
                        const label = chart.data.labels[index];
                        const value = chart.data.datasets[datasetIndex].data[index];
                  
                        console.log("Clicked slice:", { label, value });
                        handleSliceClick(label, value);
                    }}
                    />
                    {tooltipData && renderTooltip()}
                    <div
                        style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        cursor: 'pointer'
                        }}
                        onClick={()=> handleSliceClick(null, null)}
                    >
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#13135c' }}>{accounting.formatMoney(receivedRent, '€', 0, '.', ',', '%v %s')}</div>
                        <div className='rent-income-control-dashboard-card-light-grey-text'>erhalten</div>
                    </div>
                </div>
                :
                <EmptyStateDashboard
                heading={'Keine Daten verfügbar.'}
                subheading={'Es wurden noch keine Daten zu Mieteingängen gefunden.'}
                className="emptystate-dashboard empty-state-btn-added"
                icon={RentIcon}
                buttonDisable={false}
                mainContainerStyling={{width: '100%'}}
                />}
                {data &&
                <Group justify='right'>
                    {hideLegend ?
                    <Popover
  opened={openPopover}
  transitionProps={{ duration: 800 }} // Moved transition duration here
>
  {/* 1. Target Wrapper */}
  <Popover.Target>
    <div 
      onMouseEnter={() => setOpenPopover(true)} 
      onMouseLeave={() => setOpenPopover(false)} 
      onTouchStart={() => setOpenPopover(!openPopover)}
    >
      <InfoIcon style={{ color: '#5555FF', cursor: 'pointer' }} />
    </div>
  </Popover.Target>

  {/* 2. Dropdown Wrapper */}
  <Popover.Dropdown>
    {renderLegend()}
  </Popover.Dropdown>
</Popover>
                    :
                    renderLegend()}
                </Group>}
            </Group>
        </Stack>
    );
}